import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import { AppKeyStatus } from "../types";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";
import { PM_HUBS } from "../utils/pmHubRoutes";
import { fromDatetimeLocalValue } from "../utils/appKycDate";

interface AddAppKycProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddAppKyc: React.FC<AddAppKycProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(
      returnPath
        ? resolveReturnDestination(returnPath, returnSearch)
        : PM_HUBS.appKyc,
    );

  const [appKeyId, setAppKeyId] = useState("");
  const [providerGatewayType, setProviderGatewayType] = useState("");
  const [providerGatewayIdentifier, setProviderGatewayIdentifier] = useState("");
  const [appKeyStatus, setAppKeyStatus] = useState<AppKeyStatus>(AppKeyStatus.ACTIVE);
  const [description, setDescription] = useState("");
  const [keyCreatedOn, setKeyCreatedOn] = useState("");
  const [keyExpiresOn, setKeyExpiresOn] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactCompanyName, setContactCompanyName] = useState("");
  const [contactCompanyWebsite, setContactCompanyWebsite] = useState("");
  const [bmContactName, setBmContactName] = useState("");
  const [bmContactEmail, setBmContactEmail] = useState("");
  const [itSupportName, setItSupportName] = useState("");
  const [itSupportEmail, setItSupportEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const requiredFilled =
    appKeyId.trim() &&
    providerGatewayType.trim() &&
    providerGatewayIdentifier.trim();

  const handleSubmit = async () => {
    if (!requiredFilled) {
      setErrorMessage("App Key ID, Provider Gateway Type, and Provider Gateway Identifier are required.");
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await apiService.createAppKyc({
        tenantId,
        appKeyId: appKeyId.trim(),
        providerGatewayType: providerGatewayType.trim(),
        providerGatewayIdentifier: providerGatewayIdentifier.trim(),
        appKeyStatus,
        description: description || undefined,
        keyCreatedOn: fromDatetimeLocalValue(keyCreatedOn),
        keyExpiresOn: fromDatetimeLocalValue(keyExpiresOn),
        contactName: contactName || undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
        contactCompanyName: contactCompanyName || undefined,
        contactCompanyWebsite: contactCompanyWebsite || undefined,
        bmContactName: bmContactName || undefined,
        bmContactEmail: bmContactEmail || undefined,
        itSupportName: itSupportName || undefined,
        itSupportEmail: itSupportEmail || undefined,
      });
      setSuccessOpen(true);
      setTimeout(goBack, 800);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to create App KYC");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add App KYC"
        pageDescription="Register app key identity, gateway details, contacts, and provider metadata."
      />
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper sx={{ mt: 3, mb: 3, p: 3, border: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            App key & gateway *
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <TextField
              label="App Key ID *"
              value={appKeyId}
              onChange={(e) => setAppKeyId(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Provider Gateway Type *"
              value={providerGatewayType}
              onChange={(e) => setProviderGatewayType(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Provider Gateway Identifier *"
              value={providerGatewayIdentifier}
              onChange={(e) => setProviderGatewayIdentifier(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label="App Key Status *"
              value={appKeyStatus}
              onChange={(e) => setAppKeyStatus(e.target.value as AppKeyStatus)}
              fullWidth
            >
              {Object.values(AppKeyStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Key dates
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <TextField
              label="Key Created On"
              type="datetime-local"
              value={keyCreatedOn}
              onChange={(e) => setKeyCreatedOn(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Key Expires On"
              type="datetime-local"
              value={keyExpiresOn}
              onChange={(e) => setKeyExpiresOn(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Contact information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField label="Contact Name" value={contactName} onChange={(e) => setContactName(e.target.value)} fullWidth />
                <TextField label="Contact Email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} fullWidth />
                <TextField label="Contact Phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} fullWidth />
                <TextField label="Company Name" value={contactCompanyName} onChange={(e) => setContactCompanyName(e.target.value)} fullWidth />
                <TextField label="Company Website" value={contactCompanyWebsite} onChange={(e) => setContactCompanyWebsite(e.target.value)} fullWidth />
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={500}>Provider metadata</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField label="BM Contact Name" value={bmContactName} onChange={(e) => setBmContactName(e.target.value)} fullWidth />
                <TextField label="BM Contact Email" value={bmContactEmail} onChange={(e) => setBmContactEmail(e.target.value)} fullWidth />
                <TextField label="IT Support Name" value={itSupportName} onChange={(e) => setItSupportName(e.target.value)} fullWidth />
                <TextField label="IT Support Email" value={itSupportEmail} onChange={(e) => setItSupportEmail(e.target.value)} fullWidth />
              </Box>
            </AccordionDetails>
          </Accordion>

          {errorMessage && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>}

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting || !requiredFilled}>
              {submitting ? "Creating..." : "Add App KYC"}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">App KYC created successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddAppKyc;
